/* Supervisor detail — one person, and what they are responsible for.
   No mockup; `docs/design/merch-detail.html` is the visual reference for a person page. Spec/03
   archetype B, minus the parts this record cannot use: the API exposes no writable field on a
   supervisor except `is_active`, so there is no view/edit toggle, no dirty state and no Save —
   it is a read-only profile with one lifecycle action.

   **There is deliberately no sticky command bar.** Archetype B's is there for a long form whose
   Save has to stay in reach; this page has one action and short content, so the actions sit in
   the page head and the whole sticky-layering apparatus (measured offsets, ResizeObserver,
   hit-testing at both breakpoints) is not paid for. That machinery has shipped broken four times
   — the cheapest way to not break it a fifth is to not need it.

   ONE TAB IS MOUNTED AT A TIME (spec/03). antd `<Tabs destroyOnHidden>` unmounts the inactive
   pane; each tab fetches on first open and its page is cached in `tabData`, so returning to a
   tab costs nothing but only one occupies the DOM. Both tab counts come from the single
   `GET supervisors/:id/` (`points_count` / `merchandisers_count`), so the rail is complete
   before either tab has loaded.

   Two tabs, both editable, each needing a filter the endpoint did not have:
   - points — `points/?responsible_supervisors=<id>` (new `PointWebFilter` in picsell-api)
   - team   — `merchandisers/?supervisor=<id>` (new `MerchandiserWebFilter`)

   A visits tab was built and removed: `/visit` already lists them, filtered by supervisor, with
   the filters, sorting and review workspace this page would only have restated in miniature. */

import React, { useCallback, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useHistory, useLocation, useParams } from "react-router-dom";
import {
  Alert as AntAlert,
  Avatar,
  Breadcrumb,
  Button,
  Card,
  Col,
  Descriptions,
  Empty,
  Flex,
  Modal,
  Row,
  Skeleton,
  Space,
  Table,
  Tabs,
  Tag,
  Tooltip,
  Typography,
} from "antd";
import {
  CheckCircleOutlined,
  CompassOutlined,
  DeleteOutlined,
  IdcardOutlined,
  MinusCircleOutlined,
  PhoneOutlined,
  PlusOutlined,
  PushpinOutlined,
} from "@ant-design/icons";

import Alert from "/src/util/alert";

import EntitySelect from "../../component/EntitySelect";
import { PersonCell, RowActions, StatusIcon, TableFoot, TitleCell, actionsColumn, dash, fullName, initialsOf, statusColumn } from "../../component/table-cells";
import {
  ENTITY_DROPDOWN_SETTINGS,
  OVERVIEW,
  POINTS_ADMIN,
  PROFILE_PAGE,
  SIDE_NAV,
  SUPERVISOR_DETAIL,
  SUPERVISOR_LIST,
  TRANSLATION_DOWNLOADER,
  VISIT_PAGE,
} from "../../config/lang";
import { call } from "../../util/action";
import { fill, translate } from "../../util/util";

import "./supervisors.scss";

const TABS = ["points", "team"];

/* Both tabs are editable; this is the entity each one's picker offers. */
const ASSIGNABLE = {
  points: { path: "points", entity: "points" },
  team: { path: "merchandisers", entity: "merchandisers" },
};

const PAGE_SIZES = [10, 25, 50];

const Page = () => {
  const { id } = useParams();
  const history = useHistory();
  const location = useLocation();
  const me = useSelector(state => state.me);
  const lang = me?.language || "en";

  const t = key => translate(SUPERVISOR_DETAIL, key, lang);
  const tl = key => translate(SUPERVISOR_LIST, key, lang);
  const td = key => translate(ENTITY_DROPDOWN_SETTINGS, key, lang);
  const tpa = key => translate(POINTS_ADMIN, key, lang);
  const tv = key => translate(VISIT_PAGE, key, lang);

  const [item, setItem] = useState(null);
  const [status, setStatus] = useState("loading"); // loading | ready | error
  const [busy, setBusy] = useState(false);

  const initialTab = new URLSearchParams(location.search).get("t");
  const [tab, setTab] = useState(TABS.includes(initialTab) ? initialTab : "points");
  /* Per tab: `{ rows, count }` once loaded, `null` if the request failed, absent if never
     opened. Paging is server-side, so a tab holds the CURRENT page only and its `count` is the
     true total — which is what the pager and the tab badge both read. */
  const [tabData, setTabData] = useState({});
  const [tabPage, setTabPage] = useState({ points: 1, team: 1 });
  const [tabSize, setTabSize] = useState(PAGE_SIZES[1]);
  const loadingTabs = useRef(new Set());

  /* The assignment picker: which tab it is adding to, and what has been picked so far. */
  const [picker, setPicker] = useState(null);
  const [picked, setPicked] = useState([]);

  /* ---------------------------------------------------------------- record */

  const load = useCallback(() => {
    setStatus("loading");
    call({ method: "GET", url: `supervisors/${id}/` }, response => {
      if (!response?.id) return setStatus("error");
      setItem(response);
      setStatus("ready");
    });
  }, [id]);

  useEffect(load, [load]);

  /* The request each tab runs on. One place where the three parameter shapes are visible. */
  const tabUrl = useCallback(
    (key, page, size) => {
      const paging = `page=${page}&page_size=${size}`;
      if (key === "points") return `points/?responsible_supervisors=${id}&${paging}&ordering=name`;
      return `merchandisers/?supervisor=${id}&${paging}`;
    },
    [id]
  );

  const loadTab = useCallback(
    (key, page) => {
      const stamp = `${key}:${page}:${tabSize}`;
      if (loadingTabs.current.has(stamp)) return;
      loadingTabs.current.add(stamp);
      setTabData(prev => ({ ...prev, [key]: undefined }));
      call({ method: "GET", url: tabUrl(key, page, tabSize) }, response => {
        loadingTabs.current.delete(stamp);
        setTabData(prev => ({ ...prev, [key]: response?.results ? { rows: response.results, count: response.count ?? response.results.length } : null }));
      });
    },
    [tabUrl, tabSize]
  );

  /* Hydrate on demand: a tab fetches the first time it is opened, and its page stays cached, so
     coming back to it costs nothing. Paging and assignment refetch it explicitly. */
  useEffect(() => {
    if (status !== "ready" || tabData[tab] !== undefined) return;
    if ([...loadingTabs.current].some(stamp => stamp.startsWith(`${tab}:`))) return;
    loadTab(tab, tabPage[tab]);
  }, [tab, status, tabData, tabPage, loadTab]);

  /* The open tab rides in the URL, so a reload or a shared link lands in place. */
  useEffect(() => {
    const next = new URLSearchParams(location.search);
    if (next.get("t") === tab) return;
    next.set("t", tab);
    history.replace({ search: next.toString() });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  /* ------------------------------------------------- activate / deactivate */

  const setActive = value => {
    const run = () => {
      setBusy(true);
      call({ method: "PATCH", url: `supervisors/${id}/`, post: { is_active: value } }, response => {
        setBusy(false);
        if (!response) return;
        setItem(prev => ({ ...prev, is_active: value }));
        Alert.success(fill(translate(SUPERVISOR_LIST, value ? "activated_msg" : "deactivated_msg", lang), { n: 1 }), { position: "top-right" });
      });
    };
    if (value) return run();
    /* Destructive, so it is confirmed and the confirm carries the verb, never "OK" (spec/03). */
    Modal.confirm({
      title: fill(tl("confirm_deactivate"), { n: 1 }),
      okText: tl("deactivate"),
      okButtonProps: { danger: true },
      /* Static `Modal.confirm` renders outside the tree, so it does not inherit AppShell's
         `ConfigProvider locale` — without this the Cancel button stays English. */
      cancelText: translate(TRANSLATION_DOWNLOADER, "cancel", lang),
      onOk: run,
    });
  };

  /* ------------------------------------------------------------- assignment */

  /**
   * Attach or detach members of one of the supervisor's sets.
   *
   * `POST supervisors/:id/(points|merchandisers)/ {ids, detach}` — add/remove rather than a
   * PATCH replacing the whole set, because this page only ever holds ONE PAGE of the relation
   * and a replace would silently unassign everything not on screen. The server answers with the
   * refreshed counts, so the tab badges and the coverage rail move without re-reading the record.
   */
  const assign = (key, ids, detach) => {
    const { path } = ASSIGNABLE[key];
    setBusy(true);
    call({ method: "POST", url: `supervisors/${id}/${path}/`, post: { ids, detach: Boolean(detach) } }, response => {
      setBusy(false);
      if (!response || response.changed == null) {
        Alert.error(t("assign_error"), { position: "top-right" });
        return;
      }
      setItem(prev => ({
        ...prev,
        points_count: response.points_count ?? prev.points_count,
        merchandisers_count: response.merchandisers_count ?? prev.merchandisers_count,
      }));
      /* Removing the last row of the last page would otherwise leave the pager past the end. */
      const page = detach && tabData[key]?.rows.length === ids.length && tabPage[key] > 1 ? tabPage[key] - 1 : tabPage[key];
      setTabPage(prev => ({ ...prev, [key]: page }));
      loadTab(key, page);
      Alert.success(fill(t(detach ? "removed_msg" : "added_msg"), { n: response.changed }), { position: "top-right" });
      /* Ids the server refused — out of company scope, or the wrong role for the relation. */
      if (response.ignored) Alert.warning(fill(t("ignored_msg"), { n: response.ignored }), { position: "top-right" });
    });
  };

  const confirmRemove = (key, row, label) =>
    Modal.confirm({
      title: fill(t(key === "points" ? "confirm_remove_point" : "confirm_remove_merchandiser"), { name: label }),
      okText: t("remove"),
      okButtonProps: { danger: true },
      cancelText: translate(TRANSLATION_DOWNLOADER, "cancel", lang),
      onOk: () => assign(key, [row.id], true),
    });

  const submitPicker = () => {
    const ids = picked.map(option => option?.id ?? option?.value).filter(Boolean);
    if (!ids.length) return setPicker(null);
    assign(picker, ids, false);
    setPicker(null);
    setPicked([]);
  };

  /* ------------------------------------------------------------- tab tables */

  /* The row action is a MENU, not a bare icon button, because both list pages put row actions in
     one — same affordance, same place, and a second action later does not move the first. */
  const removeColumn = key =>
    actionsColumn((value, row) => (
      <RowActions
        label={tv("row_actions")}
        items={[{ key: "remove", icon: <DeleteOutlined />, label: t("remove"), danger: true }]}
        onClick={() => confirmRemove(key, row, key === "points" ? row.name : fullName(row))}
      />
    ));

  /* Both tables are built from the same primitives as the supervisors and visits lists
     (`component/table-cells.js`): the leading status glyph, the avatar-plus-name cell, the
     two-line title cell and the row-action menu. Three tables of the same records reading as
     three different products is exactly what this replaced. */
  const TAB_COLUMNS = {
    points: [
      {
        key: "name",
        title: tpa("f_name"),
        ellipsis: true,
        render: (value, row) => <TitleCell title={row.name} sub={[row.locality?.name, row.address].filter(Boolean).join(" · ")} />,
      },
      { key: "network", title: tpa("f_network"), width: 180, ellipsis: true, render: (value, row) => row.network?.name || dash },
      {
        key: "quality",
        title: "",
        width: 150,
        /* A point with no coordinates cannot be geofenced, so a visit there can never be
           verified — the same data-quality signal the points list leads with. */
        render: (value, row) =>
          row.location ? null : (
            <Tooltip title={t("no_coordinates")}>
              <Tag className="sv-tag--warn">{t("no_coordinates")}</Tag>
            </Tooltip>
          ),
      },
      removeColumn("points"),
    ],
    team: [
      /* The status is the ICON the supervisors list uses, not a word in a column of its own —
         same signal, same position, one less column competing with the name. */
      statusColumn((value, row) => (
        <StatusIcon
          Icon={row.is_active ? CheckCircleOutlined : MinusCircleOutlined}
          tone={row.is_active ? "ok" : "off"}
          label={tl(row.is_active ? "status_active" : "status_inactive")}
        />
      )),
      {
        key: "name",
        title: td("merchandisers"),
        ellipsis: true,
        render: (value, row) => <PersonCell user={row} />,
      },
      { key: "phone", title: tpa("f_phone"), width: 180, render: (value, row) => row.phone_number || dash },
      removeColumn("team"),
    ],
  };

  /* ------------------------------------------------------------------ render */

  if (status === "loading") {
    return (
      <Flex vertical gap="middle" className="shell-content lp sd">
        <Skeleton active avatar paragraph={{ rows: 2 }} />
        <Skeleton active title={false} paragraph={{ rows: 6 }} />
      </Flex>
    );
  }

  if (status === "error" || !item) {
    return (
      <Flex vertical gap="middle" className="shell-content lp sd">
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={
            <Flex vertical gap={4}>
              <Typography.Text strong>{t("not_found_title")}</Typography.Text>
              <Typography.Text type="secondary">{t("not_found_desc")}</Typography.Text>
            </Flex>
          }>
          <Button onClick={() => history.push("/supervisors")}>{tpa("back_btn")}</Button>
        </Empty>
      </Flex>
    );
  }

  const name = fullName(item) || "—";
  const role = translate(PROFILE_PAGE, item.role === "supervisor_manager" ? "role_supervisor_manager" : "role_supervisor", lang);

  const countOf = { points: item.points_count, team: item.merchandisers_count };
  const tabLabel = { points: td("points"), team: td("merchandisers") };

  const tabItems = TABS.map(key => ({
    key,
    label: (
      <Space size={6}>
        {tabLabel[key]}
        {countOf[key] != null ? <span className="sd-cnt">{countOf[key]}</span> : null}
      </Space>
    ),
    children: (
      <Flex vertical gap="small" className="sd-tab">
        {ASSIGNABLE[key] ? (
          <Flex justify="flex-end">
            <Button
              type="primary"
              icon={<PlusOutlined />}
              disabled={busy}
              onClick={() => {
                setPicked([]);
                setPicker(key);
              }}>
              {t(key === "points" ? "add_points" : "add_merchandisers")}
            </Button>
          </Flex>
        ) : null}

        {tabData[key] === undefined ? (
          <Skeleton active title={false} paragraph={{ rows: 4 }} />
        ) : tabData[key] === null ? (
          <AntAlert
            type="error"
            showIcon
            message={t("load_error")}
            action={
              <Button size="small" onClick={() => loadTab(key, tabPage[key])}>
                {translate(OVERVIEW, "retry", lang)}
              </Button>
            }
          />
        ) : (
          <>
            <Table
              className="lp-table__grid"
              rowKey="id"
              size="middle"
              columns={TAB_COLUMNS[key]}
              dataSource={tabData[key].rows}
              pagination={false}
              scroll={{ x: 620 }}
              locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t("empty_tab")} /> }}
            />
            {/* Paging is server-side: the tab holds one page, and `count` is the true total —
                the same number the tab badge shows, so the two can never disagree. */}
            <TableFoot
              id={`sd-${key}`}
              page={tabPage[key]}
              size={tabSize}
              total={tabData[key].count}
              sizes={PAGE_SIZES}
              labels={{ perPage: tv("rows_per_page"), range: tv("range_of") }}
              onPage={next => {
                setTabPage(prev => ({ ...prev, [key]: next }));
                loadTab(key, next);
              }}
              onSize={next => {
                setTabSize(next);
                setTabPage(prev => ({ ...prev, [key]: 1 }));
                /* Every tab's cached page was sized by the old value, so they all go. */
                setTabData({});
              }}
            />
          </>
        )}
      </Flex>
    ),
  }));

  return (
    <Flex vertical gap="middle" className="shell-content lp sd">
      <Breadcrumb items={[{ title: <a onClick={() => history.push("/supervisors")}>{translate(SIDE_NAV, "supervisors", lang)}</a> }, { title: name }]} />

      <Flex className="lp-head" justify="space-between" align="flex-end" gap="middle" wrap>
        <Flex vertical>
          <Typography.Title level={1} className="lp-title">
            {name}
          </Typography.Title>
          <Typography.Paragraph type="secondary" className="lp-sub">
            {[role, item.county_name].filter(Boolean).join(" · ")}
          </Typography.Paragraph>
        </Flex>
        <Space wrap>
          <Button onClick={() => history.push("/supervisors")}>{tpa("back_btn")}</Button>
          {item.is_active ? (
            <Button danger icon={<MinusCircleOutlined />} loading={busy} onClick={() => setActive(false)}>
              {tl("deactivate")}
            </Button>
          ) : (
            <Button type="primary" icon={<CheckCircleOutlined />} loading={busy} onClick={() => setActive(true)}>
              {tl("activate")}
            </Button>
          )}
        </Space>
      </Flex>

      <Row gutter={[16, 16]}>
        <Col xs={24} xl={16}>
          <Flex vertical gap="middle">
            <Card className="sd-id" variant="outlined">
              <Flex align="center" gap="middle" wrap>
                {/* Initials, not a generic glyph — see `PersonCell`. antd falls back to the children when
                    a stored photo 404s, which is common on old records. */}
                <Avatar size={64} className="sd-id__av" style={{ fontSize: 20 }} src={item.photo || null}>
                  {initialsOf(name)}
                </Avatar>
                <Flex vertical flex="1 1 240px">
                  <Typography.Text strong className="sd-id__t">
                    {name}
                  </Typography.Text>
                  <Flex className="sd-id__sub" align="center" gap="small" wrap>
                    <span>
                      <IdcardOutlined /> {role}
                    </span>
                    <span>
                      <PhoneOutlined /> {item.phone_number || "—"}
                    </span>
                    <span>
                      <CompassOutlined /> {item.county_name || tl("no_region")}
                    </span>
                  </Flex>
                </Flex>
                <StatusIcon
                  Icon={item.is_active ? CheckCircleOutlined : MinusCircleOutlined}
                  tone={item.is_active ? "ok" : "off"}
                  label={tl(item.is_active ? "status_active" : "status_inactive")}
                />
                <Typography.Text type={item.is_active ? undefined : "secondary"}>{tl(item.is_active ? "status_active" : "status_inactive")}</Typography.Text>
              </Flex>
            </Card>

            <Card className="sd-tabs" variant="outlined">
              {/* `destroyOnHidden` is what makes "one section at a time" true rather than
                  `display: none` — the inactive pane is unmounted, not hidden. */}
              <Tabs activeKey={tab} onChange={setTab} items={tabItems} destroyOnHidden />
            </Card>
          </Flex>
        </Col>

        <Col xs={24} xl={8}>
          <Flex vertical gap="middle">
            <Card className="sd-rail" variant="outlined" title={t("coverage")}>
              {/* Numbers stay off their nouns (spec/04): a label, then a figure. */}
              <Flex className="sd-stats" gap="middle" wrap>
                {[
                  ["points", <PushpinOutlined key="i" />],
                  ["team", <IdcardOutlined key="i" />],
                ].map(([key, icon]) => (
                  <Flex key={key} vertical className="sd-stat">
                    <span className="sd-stat__label">
                      {icon} {tabLabel[key]}
                    </span>
                    <span className="sd-stat__n">{countOf[key] ?? "—"}</span>
                  </Flex>
                ))}
              </Flex>
              {!item.points_count ? <AntAlert className="sd-warn" type="warning" showIcon message={tl("no_points_hint")} /> : null}
            </Card>

            <Card className="sd-rail" variant="outlined" title={t("account")}>
              <Descriptions column={1} size="small" colon={false}>
                <Descriptions.Item label={tpa("f_phone")}>{item.phone_number || <span className="lp-dash">—</span>}</Descriptions.Item>
                <Descriptions.Item label={tpa("f_county")}>{item.county_name || <span className="lp-dash">—</span>}</Descriptions.Item>
                <Descriptions.Item label={t("f_telegram")}>
                  <Typography.Text type={item.telegram_id ? undefined : "secondary"}>
                    {t(item.telegram_id ? "telegram_linked" : "telegram_none")}
                  </Typography.Text>
                </Descriptions.Item>
                <Descriptions.Item label={tpa("f_status")}>{tpa(item.is_active ? "active" : "inactive")}</Descriptions.Item>
              </Descriptions>
            </Card>
          </Flex>
        </Col>
      </Row>

      {/* The picker. `EntitySelect` is the same server-searched, page-on-scroll dropdown the
          filter rows use — the catalogue is thousands of points, so it cannot be a plain list.
          It offers everything in the company, including what is already assigned: the server
          ignores duplicates (`.add()` is idempotent) and reports them back as `ignored`. */}
      <Modal
        open={Boolean(picker)}
        title={picker ? t(picker === "points" ? "add_points" : "add_merchandisers") : ""}
        okText={t("add")}
        okButtonProps={{ disabled: !picked.length, loading: busy }}
        cancelText={translate(TRANSLATION_DOWNLOADER, "cancel", lang)}
        onOk={submitPicker}
        onCancel={() => setPicker(null)}
        destroyOnHidden>
        {picker ? (
          <EntitySelect
            className="sd-picker"
            entity={ASSIGNABLE[picker].entity}
            isMulti
            value={picked}
            /* Deliberately NOT scoped to the supervisor's own county: responsibility does not
               have to follow the region on their profile, and a picker that quietly omits valid
               choices is worse than one that lists a few too many. Company scope is enforced
               server-side, which is the boundary that matters. */
            ariaLabel={t(picker === "points" ? "add_points" : "add_merchandisers")}
            placeholder={t("picker_ph")}
            onChange={setPicked}
          />
        ) : null}
      </Modal>
    </Flex>
  );
};

export default Page;
